import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Heart, Plus, Minus, ArrowLeft, ArrowRight, CreditCard } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import ProgressBar from '../components/Checkout/ProgressBar';
import { getPaymentTypeWithInstallment } from '../utils/installmentUtils';
import { mapPaymentMethodToDisplay } from '../utils/paymentUtils';

const Cart = () => {
  const { 
    items, 
    removeFromCart, 
    updateQuantity, 
    addToWishlist, 
    removeFromWishlist, 
    getSubtotal, 
    getCartCount,
    isInWishlist 
  } = useCart();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity >= 1) {
      updateQuantity(productId, newQuantity);
    }
  };

  const handleRemoveFromCart = (productId) => {
    removeFromCart(productId);
  };

  const handleWishlistToggle = (product) => {
    if (isInWishlist(product.id)) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleContinueShopping = () => {
    navigate('/products');
  };

  const handleProceedToCheckout = () => {
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
        <ProgressBar currentStep="cart" />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 py-16">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
              <ArrowLeft className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Your cart is empty
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-12">
              Looks like you haven't added any products to your cart yet.
            </p>
            <button
              onClick={handleContinueShopping}
              className="card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-10 py-4 rounded-2xl font-bold hover:scale-105 transition-all duration-300 shadow-glow"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-blue-50 to-indigo-50 dark:from-slate-900 dark:via-purple-900 dark:to-slate-900">
      <ProgressBar currentStep="cart" />
      
      <div className="max-w-10xl mx-auto px-4 sm:px-8 lg:px-12 py-12">
        <div className="card-3d bg-white/80 dark:bg-white/10 backdrop-blur-md rounded-3xl shadow-xl border border-white/20">
          {/* Header */}
          <div className="px-6 sm:px-8 py-6 border-b border-gray-200 dark:border-gray-700">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Shopping Cart ({getCartCount()} items)
            </h1>
          </div>

          {/* Cart Items */}
          <div className="overflow-x-auto">
            <table className="w-full hidden md:table">
              <thead className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                <tr>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Product
                  </th>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Category
                  </th>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Quantity
                  </th>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Price
                  </th>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Total
                  </th>
                  <th className="px-8 py-6 text-left text-sm font-bold text-gray-900 dark:text-white">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => {
                  // Use calculated price if available, otherwise fall back to original calculation
                  const price = item.calculatedPrice || parseFloat(item.regular_license_price || 0);
                  const total = price * item.quantity;
                  
                  return (
                    <tr key={item.id} className="hover:bg-white/50 dark:hover:bg-white/5 transition-all duration-300">
                      <td className="px-8 py-6">
                        <div className="flex items-center">
                          <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl overflow-hidden mr-6 shadow-lg">
                            {item.type === 'payment' ? (
                              <div className="w-full h-full flex items-center justify-center text-white">
                                <CreditCard className="w-10 h-10" />
                              </div>
                            ) : item.image ? (
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-white">
                                <span className="text-sm font-semibold">No Image</span>
                              </div>
                            )}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-2">
                              {item.name}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-300 mb-3">
                              {item.category_name || item.category}
                            </p>
                            
                            {/* Display payment-specific information */}
                            {item.type === 'payment' && item.paymentData && (
                              <div className="space-y-2">
                                <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
                                  Type: {getPaymentTypeWithInstallment(item.paymentData.type, item.paymentData.installment_number)}
                                </p>
                                {item.paymentData.next_payment_date && (
                                  <p className="text-sm text-green-600 dark:text-green-400">
                                    Due Date: {new Date(item.paymentData.next_payment_date).toLocaleDateString()}
                                  </p>
                                )}
                                {item.paymentData.payment_method && !item.paymentData.payment_method.toLowerCase().includes('cash on delivery') && (
                                  <p className="text-sm text-purple-600 dark:text-purple-400">
                                    Method: {mapPaymentMethodToDisplay(item.paymentData.payment_method)}
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Display selected options for products */}
                            {!item.type && item.selectedLicense && (
                              <div className="space-y-2">
                                <p className="text-sm text-blue-600 dark:text-blue-400 font-semibold">
                                  License: {item.selectedLicense === 'regular' ? 'Regular' : 'Extended'}
                                </p>
                                {item.selectedAddons && item.selectedAddons.length > 0 && (
                                  <p className="text-sm text-purple-600 dark:text-purple-400">
                                    Add-ons: {item.selectedAddons.length} selected
                                  </p>
                                )}
                                {item.extendedSupport && (
                                  <p className="text-sm text-green-600 dark:text-green-400">
                                    Extended Support: Yes
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-gray-900 dark:text-white font-semibold">
                          {item.category_name || item.category}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        {item.type === 'payment' ? (
                          <span className="text-gray-900 dark:text-white font-bold">
                            Fixed (1)
                          </span>
                        ) : (
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity - 1)}
                              className="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110"
                            >
                              <Minus className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                            <span className="w-16 text-center text-gray-900 dark:text-white font-bold text-lg">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() => handleQuantityChange(item.id, item.quantity + 1)}
                              className="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110"
                            >
                              <Plus className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-gray-900 dark:text-white font-bold text-lg">
                          {formatPrice(price)}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <span className="text-blue-600 dark:text-blue-400 font-bold text-xl">
                          {formatPrice(total)}
                        </span>
                      </td>
                      <td className="px-8 py-6">
                        <div className="flex items-center space-x-3">
                          {item.type !== 'payment' && (
                            <button
                              onClick={() => handleWishlistToggle(item)}
                              className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${
                                isInWishlist(item.id)
                                  ? 'text-red-500 hover:text-red-600 bg-red-50 dark:bg-red-900/20'
                                  : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                              }`}
                              title={isInWishlist(item.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                            >
                              <Heart className={`w-5 h-5 ${isInWishlist(item.id) ? 'fill-current' : ''}`} />
                            </button>
                          )}
                          <button
                            onClick={() => handleRemoveFromCart(item.id)}
                            className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full transition-all duration-300 hover:scale-110"
                            title="Remove from cart"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Items List */}
          <div className="px-6 sm:px-8 py-6 md:hidden">
            <div className="space-y-6">
              {items.map((item) => {
                const price = item.calculatedPrice || parseFloat(item.regular_license_price || 0);
                const total = price * item.quantity;
                return (
                  <div key={item.id} className="flex items-start space-x-4 bg-white/60 dark:bg-white/5 rounded-2xl p-4 border border-white/20">
                    <div className="w-24 h-24 flex-shrink-0 rounded-xl overflow-hidden bg-gradient-to-br from-blue-500 to-purple-600">
                      {item.type === 'payment' ? (
                        <div className="w-full h-full flex items-center justify-center text-white">
                          <CreditCard className="w-10 h-10" />
                        </div>
                      ) : item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-sm">No Image</div>
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-bold text-gray-900 dark:text-white text-base">{item.name}</h3>
                          <p className="text-gray-600 dark:text-gray-300 text-sm">{item.category_name || item.category}</p>
                        </div>
                        <button onClick={() => handleRemoveFromCart(item.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-full">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {item.type !== 'payment' && (
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center space-x-2">
                            <button onClick={() => handleQuantityChange(item.id, item.quantity - 1)} className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center">
                              <Minus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                            <span className="w-10 text-center text-gray-900 dark:text-white font-bold">{item.quantity}</span>
                            <button onClick={() => handleQuantityChange(item.id, item.quantity + 1)} className="w-8 h-8 rounded-full border-2 border-gray-300 dark:border-gray-600 flex items-center justify-center">
                              <Plus className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                          </div>
                          <span className="text-blue-600 dark:text-blue-400 font-bold">{formatPrice(total)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary */}
          <div className="px-6 sm:px-8 py-8 border-t border-gray-200 dark:border-gray-700">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
              <div className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                Subtotal: <span className="text-blue-600 dark:text-blue-400">{formatPrice(getSubtotal())}</span>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 w-full sm:w-auto">
                <button
                  onClick={handleContinueShopping}
                  className="w-full sm:w-auto px-6 py-3 sm:px-8 sm:py-4 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-300 font-semibold"
                >
                  Continue Shopping
                </button>
                <button
                  onClick={handleProceedToCheckout}
                  className="w-full sm:w-auto card-3d bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 sm:px-10 sm:py-4 rounded-2xl font-bold transition-all duration-300 flex items-center justify-center gap-2 shadow-glow"
                >
                  <span>Next</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart; 